import { Filters } from '@/types/Filters';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';

/**
 * Table filters in the address bar: a filtered list can be shared as a link,
 * survives a refresh, and comes back with the back button. Only these keys
 * are read, so a hand-edited link cannot add fields the list endpoint would
 * reject.
 */
const NUMBER_KEYS = [
  'categoryId',
  'branchId',
  'companyId',
  'activityId',
  'childActivityId',
  'warehouseId'
] as const;
const BOOLEAN_KEYS = [
  'lowStockOnly',
  'isActive',
  'isIngredient',
  'isStockTracked',
  'trackExpiry'
] as const;
const STRING_KEYS = [
  'status',
  'search',
  'startDate',
  'endDate',
  'timezone',
  'stockLevel',
  'sortBy',
  'type',
  'role',
  'orderStatus',
  'purchaseType',
  'stockUnit',
  'transactionType'
] as const;

/** Every query parameter these helpers own. */
export const FILTER_URL_KEYS: string[] = [
  ...NUMBER_KEYS,
  ...BOOLEAN_KEYS,
  ...STRING_KEYS,
  'sortOrder',
  'priceMin',
  'priceMax',
  'page',
  'limit'
];

const parseNumber = (value: string | null): number | undefined => {
  if (value === null || value.trim() === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : undefined;
};

/** Filters found in a query string (`?status=ACTIVE&page=2`). */
export function filtersFromSearch(search: string): Partial<Filters> {
  const params = new URLSearchParams(search);
  const filters: Partial<Filters> = {};
  const assign = filters as Record<string, unknown>;

  for (const key of NUMBER_KEYS) {
    const value = parseNumber(params.get(key));
    if (value !== undefined && Number.isInteger(value)) assign[key] = value;
  }
  for (const key of BOOLEAN_KEYS) {
    const value = params.get(key);
    if (value === 'true' || value === 'false') assign[key] = value === 'true';
  }
  for (const key of STRING_KEYS) {
    const value = params.get(key);
    if (value) assign[key] = value;
  }
  const sortOrder = params.get('sortOrder');
  if (sortOrder === 'asc' || sortOrder === 'desc') {
    filters.sortOrder = sortOrder;
  }

  const priceMin = parseNumber(params.get('priceMin'));
  const priceMax = parseNumber(params.get('priceMax'));
  if (priceMin !== undefined || priceMax !== undefined) {
    filters.price = { min: priceMin, max: priceMax };
  }

  // The address bar counts pages from 1, the API from 0.
  const page = parseNumber(params.get('page'));
  if (page !== undefined && Number.isInteger(page) && page >= 1) {
    filters.page = page - 1;
  }
  const limit = parseNumber(params.get('limit'));
  if (limit !== undefined && PAGE_SIZE_OPTIONS.includes(limit)) {
    filters.limit = limit;
  }
  return filters;
}

const isEmpty = (value: unknown) =>
  value === undefined || value === null || value === '';

/**
 * `currentSearch` with the filter keys replaced by `filters`. Values equal to
 * `defaults` are left out to keep links short; other parameters (e.g. a tab)
 * are kept.
 */
export function searchWithFilters(
  currentSearch: string,
  filters: Filters,
  defaults: Filters
): string {
  const params = new URLSearchParams(currentSearch);
  for (const key of FILTER_URL_KEYS) params.delete(key);

  const source = filters as Record<string, unknown>;
  const base = defaults as Record<string, unknown>;
  for (const key of [
    ...NUMBER_KEYS,
    ...BOOLEAN_KEYS,
    ...STRING_KEYS,
    'sortOrder'
  ]) {
    const value = source[key];
    if (isEmpty(value) || value === base[key]) continue;
    params.set(key, String(value));
  }
  if (!isEmpty(filters.price?.min)) {
    params.set('priceMin', String(filters.price!.min));
  }
  if (!isEmpty(filters.price?.max)) {
    params.set('priceMax', String(filters.price!.max));
  }
  if (filters.page && filters.page > 0) {
    params.set('page', String(filters.page + 1));
  }
  if (filters.limit && filters.limit !== defaults.limit) {
    params.set('limit', String(filters.limit));
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}

/** `currentSearch` without any filter key. */
export function searchWithoutFilters(currentSearch: string): string {
  const params = new URLSearchParams(currentSearch);
  for (const key of FILTER_URL_KEYS) params.delete(key);
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * Rewrites the current history entry's query without a navigation (no
 * refetch, no new history entry). The router's own state is kept.
 */
export function replaceSearch(search: string): void {
  const { pathname, hash, search: current } = window.location;
  if (search === current) return;
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${search}${hash}`
  );
}
